import React, { useContext, useEffect, useRef, useState } from "react";
import { ShoppingCart, ArrowLeft, CreditCard, Shield } from "lucide-react";
import ApiService from "../api/ApiService";
import { useNavigate } from 'react-router-dom';
import '../css/ShopListNew.css';
import { loadRazorpayScript } from "../utils/common";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { Trash2, Plus, Minus } from "lucide-react";
import AppContext from "../context/AppContext";
import { emailRegex } from "../constants/common";

const CheckoutForm = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const reCaptChaRef = useRef(null);
  const { donor } = useContext(AppContext);

  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    mode: "onBlur",
  });

  useEffect(() => {
    const cartData = localStorage.getItem('cartData');
    if (cartData) {
      setCart(JSON.parse(cartData));
    } else {
      navigate('/shop-new');
    }
  }, [navigate]);

  useEffect(() => {
    if (!donor) return;
    const address = donor?.addresses?.[0] ?? donor?.default_address ?? {};

    reset({
      name: donor?.legal_name || '',
      phone: donor?.phone || '',
      email: donor?.email || '',
      pin: address?.pincode || '',
      city: address?.city || '',
      address: `${address?.address_line_1 ?? ''} ${address?.address_line_2 ?? ''}`.trim()
    });
  }, [donor, reset]);

  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    const updatedCart = cart.map(item =>
      item._id === productId ? { ...item, cartQuantity: newQuantity } : item
    );
    setCart(updatedCart);
    localStorage.setItem('cartData', JSON.stringify(updatedCart));
  };

  const removeFromCart = (productId) => {
    const updatedCart = cart.filter(item => item._id !== productId);
    setCart(updatedCart);
    localStorage.setItem('cartData', JSON.stringify(updatedCart));

    if (updatedCart.length === 0) {
      localStorage.removeItem('cartData');
      navigate('/shop-new');
    }
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + ((item.price || 0) * item.cartQuantity), 0);
  };

  const getCartItemsCount = () => {
    return cart.reduce((sum, item) => sum + item.cartQuantity, 0);
  };

  const onFormSubmit = async (formData) => {
    try {
      setOrderLoading(true);
      const token = await reCaptChaRef.current.executeAsync();
      reCaptChaRef.current.reset();

      const products = cart.map(item => ({
        productName: item.title,
        quantity: item.cartQuantity,
        amount: (item.price || 0) * item.cartQuantity,
        product_code: item.product_code || item._id
      }));

      const orderData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        pin: formData.pin,
        userType: 'guest',
        products,
        totalAmount: getCartTotal(),
        token
      };

      const response = await ApiService.purchaseShopProduct(orderData);
      const isRazorpayLoaded = await loadRazorpayScript();
      if (!isRazorpayLoaded) {
        alert("Razorpay SDK failed to load. Please check your connection.");
        return;
      }

      const razorpayOptions = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        currency: response.currency,
        amount: response.amount,
        name: process.env.REACT_APP_PROJECT_NAME || "Shop Purchase",
        description: "Multiple Products Purchase",
        image: `${process.env.REACT_APP_BACKEND_URL}/assets/img/logo.png`,
        order_id: response.id,
        handler: function (razorpayResponse) {
          localStorage.removeItem('cartData');
          navigate(`/purchase-success/${response.shopId}`, { replace: true });
        },
        prefill: {
          name: response.name || "",
          email: response.email || "",
          contact: response.phone || "",
        },
        notes: { type: "shop" },
        theme: { color: "#FFD370" },
        modal: {
          onDismiss: async function () {
            if (response.orderId) {
              await ApiService.cancelPurchase(response.orderId);
            }
          },
        },
      };

      const paymentObject = new window.Razorpay(razorpayOptions);
      paymentObject.open();

      paymentObject.on("payment.failed", function () {
        navigate(`/purchase-failed?id=${response.shopId}`, { replace: true });
      });
    } catch (error) {
      console.error("Order error:", error);
      alert(error.response?.data?.message || 'Unable to process order. Please try again.');
    } finally {
      setOrderLoading(false);
    }
  };

  const totalItems = getCartItemsCount();
  const totalPrice = getCartTotal();

  if (cart.length === 0) {
    return (
      <div className="empty-cart-container">
        <ShoppingCart className="empty-cart-icon" />
        <h2 className="empty-cart-title">Your cart is empty</h2>
        <p className="empty-cart-text">Add some products to get started</p>
        <button onClick={() => navigate('/shop-new')} className="continue-shopping">Continue Shopping</button>
      </div>
    );
  }

  return (
    <div className="checkout-page-container">
      <div className="container">
        <div className="checkout-header">
          <button onClick={() => navigate('/shop-new')} className="back-button">
            <ArrowLeft className="back-icon" />
            Continue Shopping
          </button>
        </div>
        <div className="checkout-content">
          <div className="cart-items">
            <h2 className="cart-items-title">Cart Items ({totalItems} items)</h2>
            {cart.map((item) => (
              <div key={item._id} className="cart-item">
                <img
                  src={`${process.env.REACT_APP_S3_URL}/uploads/shop/gallery-images/${item.galleryImages?.[0] || 'placeholder.jpg'}`}
                  alt={item.title}
                  className="cart-item-image"
                />
                <div className="cart-item-details">
                  <h3 className="cart-item-title">{item.title}</h3>
                  <div className="cart-item-price">
                    ₹{((item.price || 0) * item.cartQuantity).toLocaleString()}
                  </div>
                  <p className="cart-item-quantity-info">Contains: {item.quantity}</p>
                </div>
                <div className="cart-item-actions">
                  <div className="cart-quantity-controls">
                    <button onClick={() => updateCartQuantity(item._id, item.cartQuantity - 1)} className="cart-quantity-btn">
                      <Minus className="quantity-icon" />
                    </button>
                    <span className="cart-quantity-value">{item.cartQuantity}</span>
                    <button onClick={() => updateCartQuantity(item._id, item.cartQuantity + 1)} className="cart-quantity-btn">
                      <Plus className="quantity-icon" />
                    </button>
                  </div>
                  <button onClick={() => removeFromCart(item._id)} className="remove-item-btn">
                    <Trash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={handleSubmit(onFormSubmit)} className="checkout-section">
            <div className="customer-form">
              <h3 className="form-title">Delivery Information</h3>
              <div className="form-grid">

                <div className="form-field">
                  <input
                    type="text"
                    placeholder="Full Name *"
                    {...register("name", { required: "Full Name is required" })}
                    className={`form-input ${errors.name ? 'input-error' : ''}`}
                  />
                  {errors.name?.type === "required" && <p className="error text-danger">{errors.name?.message}</p>}
                </div>

                <div className="form-field">
                  <input
                    type="email"
                    placeholder="Email Address *"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: emailRegex,
                        message: "Please enter a valid email",
                      },
                    })}
                    className={`form-input ${errors.email ? 'input-error' : ''}`}
                  />
                  {errors.email?.type === "required" && <p className="error text-danger">{errors.email?.message}</p>}
                  {errors.email?.type === "pattern" && <p className="error text-danger">{errors.email?.message}</p>}
                </div>

                <div className="form-field">
                  <input
                    type="tel"
                    placeholder="Phone Number *"
                    {...register("phone", {
                      required: "Phone number is required",
                      pattern: {
                        value: /^[6-9]\d*$/,
                        message: "Please provide a valid phone number",
                      },
                      minLength: {
                        value: 10,
                        message: "Phone number must be at least 10 digits",
                      },
                    })}
                    className={`form-input ${errors.phone ? 'input-error' : ''}`}
                  />
                  {errors.phone?.type === "required" && <p className="error text-danger">{errors.phone?.message}</p>}
                  {errors.phone?.type === "pattern" && <p className="error text-danger">{errors.phone?.message}</p>}
                  {errors.phone?.type === "minLength" && <p className="error text-danger">{errors.phone?.message}</p>}
                </div>

                <div className="form-field">
                  <input
                    type="text"
                    placeholder="City"
                    {...register("city", { required: "City is required" })}
                    className={`form-input ${errors.city ? 'input-error' : ''}`}
                  />
                  {errors.city?.type === "required" && <p className="error text-danger">{errors.city?.message}</p>}
                </div>
              </div>

              {/* Complete Address */}
              <div className="form-field">
                <textarea
                  placeholder="Complete Address *"
                  {...register("address", { required: "Address is required" })}
                  className={`form-textarea ${errors.address ? 'input-error' : ''}`}
                  rows="3"
                />
                {errors.address?.type === "required" && <p className="error text-danger">{errors.address?.message}</p>}
              </div>

              <div className="form-field">
                <input
                  type="text"
                  placeholder="PIN Code"
                  {...register("pin", { required: "PIN Code is required" })}
                  className="form-input"
                />
                {errors.pin?.type === "required" && <p className="error text-danger">{errors.pin?.message}</p>}
              </div>
            </div>

            <div className="order-summary">
              <h3 className="summary-title">Order Summary</h3>
              <div className="summary-row">
                <span>Subtotal ({totalItems} items)</span>
                <span>₹{totalPrice.toLocaleString()}</span>
              </div>
              <div className="summary-divider"></div>
              <div className="summary-total">
                <span>Total Amount</span>
                <span>₹{totalPrice.toLocaleString()}</span>
              </div>
            </div>

            <div className="payment-section">
              <div className="security-badge">
                <Shield className="security-icon" />
                <span>Secure Payment</span>
              </div>
              <button
                type="submit"
                className="place-order-btn"
                disabled={orderLoading}
              >
                {orderLoading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="credit-card-icon" />
                    Place Order - ₹{totalPrice.toLocaleString()}
                  </>
                )}
              </button>
              <ReCAPTCHA
                ref={reCaptChaRef}
                size="invisible"
                sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
                className="recaptcha-invisible-badge"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CheckoutForm;